import { ValueAnimationTransition, animate } from 'framer-motion';
import { LinkProps } from 'next/link';
import { HTMLAttributes, MouseEventHandler, forwardRef, useState } from 'react';
import Link from '@/shared/components/shared/Link';
import { getCSSCustomProp } from '@/shared/lib/utils/css';
import { getOffsetTop } from '@/shared/lib/utils/dom';
import { easeOutQuart } from '@/shared/lib/utils/easings';

type Props = HTMLAttributes<HTMLAnchorElement> & LinkProps;

export const scrollToHref = (href: string, offsetY = 0, options?: ValueAnimationTransition<number>) => {
    const hashtagIndex = href.indexOf('#');

    const headerHeight =
        typeof window !== 'undefined' ? getCSSCustomProp(document.documentElement, '--header-height', 'number') : 0;
    const el = document.querySelector(href.substring(hashtagIndex));

    if (el) {
        const start = window.scrollY;

        const onWheel = () => {
            document.removeEventListener('wheel', onWheel);
            controls.stop();
        };

        const controls = animate(start, getOffsetTop(el, start, headerHeight as number) - 30 - offsetY, {
            duration: 1,
            ease: easeOutQuart,
            onPlay: () => {
                document.addEventListener('wheel', onWheel, { once: true });
            },
            onUpdate: (latest) => window.scrollTo({ top: latest, behavior: 'auto' }),
            onComplete: () => {
                document.removeEventListener('wheel', onWheel);
            },
            ...options,
        });
    }
};

const ScrollToLink = forwardRef<HTMLAnchorElement, Props>((props, ref) => {
    const [isAnimating, setIsAnimating] = useState(false);

    const onClick: MouseEventHandler<HTMLAnchorElement> = async (event) => {
        event.preventDefault();

        if (isAnimating) return;

        const target = event.target as HTMLElement;
        const href = target.getAttribute('href');

        if (href) {
            scrollToHref(href, 0, {
                onPlay: () => setIsAnimating(true),
                onStop: () => setIsAnimating(false),
                onComplete: () => setIsAnimating(false),
            });
        }
    };

    return (
        <Link
            {...props}
            ref={ref}
            scroll={false}
            onClick={(event) => {
                onClick(event);
                props.onClick?.(event);
            }}
        />
    );
});

ScrollToLink.displayName = 'ScrollToLink';

export default ScrollToLink;
